import { Injectable } from '@angular/core';
import * as THREE from 'three';

export interface ModelLoadResult {
  model: THREE.Object3D;
  animations?: THREE.AnimationClip[];
  size: THREE.Vector3;
  center: THREE.Vector3;
  materials?: { [key: string]: THREE.Material };
  /**
   * Origine retranchée aux sommets. Sans offset imposé par l'appelant, c'est le premier sommet
   * du fichier : laisser des coordonnées projetées absolues, de l'ordre du million, dans un
   * Float32Array fait sauter les sommets dès que la caméra bouge.
   */
  origin: THREE.Vector3;
}

export interface LoadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface MTLMaterial {
  name: string;
  ambient: THREE.Color;
  diffuse: THREE.Color;
  specular: THREE.Color;
  emissive: THREE.Color;
  shininess: number;
  opacity: number;
  diffuseMap?: string;
  normalMap?: string;
  specularMap?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ModelLoaderService {

  constructor() { }

  /**
   * Charge un modèle OBJ à partir d'un fichier File
   */
  async loadModelFromFile(
    file: File,
    offsetPoint?: THREE.Vector3,
    onProgress?: (progress: LoadProgress) => void
  ): Promise<ModelLoadResult> {
    return new Promise((resolve, reject) => {
      const fileExtension = this.getFileExtension(file.name);
      
      if (fileExtension !== 'obj') {
        reject(new Error(`Format non supporté: ${fileExtension}. Seul .obj est supporté.`));
        return;
      }

      const reader = new FileReader();
      
      reader.onprogress = (event: ProgressEvent) => {
        if (onProgress && event.lengthComputable) {
          const progress: LoadProgress = {
            loaded: event.loaded,
            total: event.total,
            percentage: Math.round((event.loaded / event.total) * 100)
          };
          onProgress(progress);
        }
      };

      reader.onload = (e) => {
        try {
          const objString = e.target?.result as string;
          const object = this.parseOBJ(objString, offsetPoint);
          this.processModel(object);

          const result: ModelLoadResult = {
            model: object,
            ...this.getModelInfo(object)
          };

          resolve(result);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error('Erreur lors de la lecture du fichier'));
      reader.readAsText(file);
    });
  }

  /**
   * Charge un OBJ avec un fichier MTL et des textures
   */
  async loadOBJWithMTLAndTextureFiles(
    objFile: File,
    mtlFile: File | null,
    textureFiles: File[],
    offsetPoint?: THREE.Vector3,
    onProgress?: (progress: LoadProgress) => void
  ): Promise<ModelLoadResult> {
    return new Promise(async (resolve, reject) => {
      try {
        let materials: { [key: string]: THREE.Material } = {};

        // Créer un map des URLs de textures
        const textureUrlMap: { [key: string]: string } = {};
        if (textureFiles && textureFiles.length > 0) {
          for (const file of textureFiles) {
            if (this.isImageFile(file.name)) {
              const url = URL.createObjectURL(file);
              textureUrlMap[file.name] = url;
            }
          }
        }

        // Charger le fichier MTL avec les textures locales
        if (mtlFile) {
          materials = await this.parseMTLFileWithLocalTextures(mtlFile, textureUrlMap, offsetPoint);
        }

        // Charger le fichier OBJ
        const reader = new FileReader();
        
        reader.onprogress = (event: ProgressEvent) => {
          if (onProgress && event.lengthComputable) {
            const progress: LoadProgress = {
              loaded: event.loaded,
              total: event.total,
              percentage: Math.round((event.loaded / event.total) * 100)
            };
            onProgress(progress);
          }
        };

        reader.onload = (e) => {
          try {
            const objString = e.target?.result as string;
            const object = Object.keys(materials).length > 0 
              ? this.parseOBJWithMaterials(objString, materials, offsetPoint)
              : this.parseOBJ(objString, offsetPoint);
            
            this.processModel(object);

            const result: ModelLoadResult = {
              model: object,
              materials: materials,
              ...this.getModelInfo(object)
            };

            resolve(result);
          } catch (error) {
            reject(error);
          }
        };

        reader.onerror = () => reject(new Error('Erreur lors de la lecture du fichier OBJ'));
        reader.readAsText(objFile);

      } catch (error) {
        reject(error);
      }
    });
  }


  private parseOBJ(objString: string, offsetPoint?: THREE.Vector3): THREE.Group {
    const group = new THREE.Group();
    const vertices: THREE.Vector3[] = [];
    const faces: number[][] = [];
    const lines = objString.split('\n');
    let origin = offsetPoint;

    for (const line of lines) {
      const trimmedLine = line.trim();
      if (!trimmedLine || trimmedLine.startsWith('#')) continue;

      const parts = trimmedLine.split(/\s+/);

      if (parts[0] === 'v' && parts.length >= 4) {

        const xDbl = Number(parts[1]);
        const yDbl = Number(parts[2]);
        const zDbl = Number(parts[3]);

        origin ??= new THREE.Vector3(xDbl, yDbl, zDbl);
        vertices.push(new THREE.Vector3(xDbl - origin.x, yDbl - origin.y, zDbl - origin.z));
      } else if (parts[0] === 'f' && parts.length >= 4) {
        const faceVertices: number[] = [];
        for (let i = 1; i < parts.length; i++) {
          const vertexData = parts[i].split('/');
          const vertexIndex = parseInt(vertexData[0]) - 1;
          if (!isNaN(vertexIndex) && vertexIndex >= 0 && vertexIndex < vertices.length) {
            faceVertices.push(vertexIndex);
          }
        }

        if (faceVertices.length >= 3) {
          for (let i = 1; i < faceVertices.length - 1; i++) {
            faces.push([faceVertices[0], faceVertices[i], faceVertices[i + 1]]);
          }
        }
      }
    }

    if (vertices.length === 0) {
      throw new Error('Aucun vertex trouvé dans le fichier OBJ');
    }

    if (faces.length === 0) {
      throw new Error('Aucune face trouvée dans le fichier OBJ');
    }

    const geometry = new THREE.BufferGeometry();
    const positionArray = new Float32Array(faces.length * 3 * 3);

    for (let i = 0; i < faces.length; i++) {
      const face = faces[i];
      for (let j = 0; j < 3; j++) {
        const vertex = vertices[face[j]];
        const arrayIndex = i * 9 + j * 3;
        positionArray[arrayIndex] = vertex.x;
        positionArray[arrayIndex + 1] = vertex.y;
        positionArray[arrayIndex + 2] = vertex.z;
      }
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positionArray, 3));
    geometry.computeVertexNormals();

    const material = new THREE.MeshPhongMaterial({
      color: 0xff6600,
      shininess: 30,
      side: THREE.DoubleSide
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.name = 'OBJ_Model';

    group.add(mesh);
    group.name = 'OBJ_Group';
    group.userData['origin'] = origin;

    return group;
  }


  private parseOBJWithMaterials(objString: string, materials: { [key: string]: THREE.Material }, offsetPoint?: THREE.Vector3): THREE.Group {
    const group = new THREE.Group();
    const vertices: THREE.Vector3[] = [];
    const uvs: THREE.Vector2[] = [];
    const faces: { indices: number[], uvIndices?: number[], material?: string }[] = [];
    const lines = objString.split('\n');

    let currentMaterial: string | undefined;
    let origin = offsetPoint;

    for (const line of lines) {
      const trimmedLine = line.trim();
      if (!trimmedLine || trimmedLine.startsWith('#')) continue;

      const parts = trimmedLine.split(/\s+/);

      if (parts[0] === 'v' && parts.length >= 4) {

        const xDbl = Number(parts[1]);
        const yDbl = Number(parts[2]);
        const zDbl = Number(parts[3]);

        origin ??= new THREE.Vector3(xDbl, yDbl, zDbl);
        vertices.push(new THREE.Vector3(xDbl - origin.x, yDbl - origin.y, zDbl - origin.z));
      } else if (parts[0] === 'vt' && parts.length >= 3) {
        const u = parseFloat(parts[1]);
        const v = parseFloat(parts[2]);
        uvs.push(new THREE.Vector2(u, v));
      } else if (parts[0] === 'usemtl' && parts.length >= 2) {
        currentMaterial = parts[1];
      } else if (parts[0] === 'f' && parts.length >= 4) {
        const faceVertices: number[] = [];
        const faceUVs: number[] = [];
        
        for (let i = 1; i < parts.length; i++) {
          const vertexData = parts[i].split('/');
          const vertexIndex = parseInt(vertexData[0]) - 1;
          
          if (!isNaN(vertexIndex) && vertexIndex >= 0 && vertexIndex < vertices.length) {
            faceVertices.push(vertexIndex);
            
            if (vertexData.length > 1 && vertexData[1]) {
              const uvIndex = parseInt(vertexData[1]) - 1;
              if (!isNaN(uvIndex) && uvIndex >= 0 && uvIndex < uvs.length) {
                faceUVs.push(uvIndex);
              } else {
                faceUVs.push(-1); 
              }
            } else {
              faceUVs.push(-1); 
            }
          }
        }

        if (faceVertices.length >= 3) {
          for (let i = 1; i < faceVertices.length - 1; i++) {
            const faceData: { indices: number[], uvIndices?: number[], material?: string } = {
              indices: [faceVertices[0], faceVertices[i], faceVertices[i + 1]],
              material: currentMaterial
            };
            
            if (faceUVs.length === faceVertices.length && 
                faceUVs[0] >= 0 && faceUVs[i] >= 0 && faceUVs[i + 1] >= 0) {
              faceData.uvIndices = [faceUVs[0], faceUVs[i], faceUVs[i + 1]];
            }
            
            faces.push(faceData);
          }
        }
      }
    }

    const facesByMaterial: { [key: string]: typeof faces } = {};
    for (const face of faces) {
      const materialName = face.material || 'default';
      if (!facesByMaterial[materialName]) {
        facesByMaterial[materialName] = [];
      }
      facesByMaterial[materialName].push(face);
    }

    for (const [materialName, materialFaces] of Object.entries(facesByMaterial)) {
      const geometry = new THREE.BufferGeometry();
      const positionArray = new Float32Array(materialFaces.length * 3 * 3);
      const uvArray = new Float32Array(materialFaces.length * 3 * 2);
      
      let hasUVs = true;
      for (const face of materialFaces) {
        if (!face.uvIndices || face.uvIndices.length !== 3) {
          hasUVs = false;
          break;
        }
      }

      for (let i = 0; i < materialFaces.length; i++) {
        const face = materialFaces[i];
        for (let j = 0; j < 3; j++) {
          const vertex = vertices[face.indices[j]];
          const arrayIndex = i * 9 + j * 3;
          positionArray[arrayIndex] = vertex.x;
          positionArray[arrayIndex + 1] = vertex.y;
          positionArray[arrayIndex + 2] = vertex.z;
          
          if (hasUVs && face.uvIndices && face.uvIndices[j] >= 0) {
            const uv = uvs[face.uvIndices[j]];
            const uvIndex = i * 6 + j * 2;
            uvArray[uvIndex] = uv.x;
            uvArray[uvIndex + 1] = uv.y;
          }
        }
      }

      const positionAttribute = new THREE.BufferAttribute(positionArray, 3);
      positionAttribute.needsUpdate = true;
      geometry.setAttribute('position', positionAttribute);
      
      if (hasUVs) {
        const uvAttribute = new THREE.BufferAttribute(uvArray, 2);
        uvAttribute.needsUpdate = true;
        geometry.setAttribute('uv', uvAttribute);
      }
      
      geometry.computeVertexNormals();
      
      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();
      
      console.log(`Mesh ${materialName}: ${materialFaces.length} triangles, hasUVs: ${hasUVs}`);

      let material: THREE.Material;
      if (materials[materialName]) {
        material = materials[materialName];
      } else {
        material = new THREE.MeshPhongMaterial({
          color: 0xff6600,
          shininess: 30,
          side: THREE.DoubleSide,
          depthWrite: true,
          depthTest: true
        });
      }

      const mesh = new THREE.Mesh(geometry, material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.name = `OBJ_Mesh_${materialName}`;

      group.add(mesh);
    }

    group.name = 'OBJ_Group';
    group.userData['origin'] = origin;
    return group;
  }

 
  private async parseMTLFileWithLocalTextures(
    mtlFile: File,
    textureUrlMap: { [key: string]: string },
    offsetPoint?: THREE.Vector3
  ): Promise<{ [key: string]: THREE.Material }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const mtlContent = e.target?.result as string;
          const materials = this.parseMTLWithLocalTextures(mtlContent, textureUrlMap, offsetPoint);
          resolve(materials);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => reject(new Error('Erreur lors de la lecture du fichier MTL'));
      reader.readAsText(mtlFile);
    });
  }

  
  private parseMTLWithLocalTextures(
    mtlContent: string,
    textureUrlMap: { [key: string]: string },
    offsetPoint?: THREE.Vector3 
  ): { [key: string]: THREE.Material } {
    const materials: { [key: string]: THREE.Material } = {};
    const lines = mtlContent.split('\n');
    let currentMaterial: MTLMaterial | null = null;
    let currentMaterialName = '';

    for (const line of lines) {
      const trimmedLine = line.trim();
      if (!trimmedLine || trimmedLine.startsWith('#')) continue;

      const parts = trimmedLine.split(/\s+/);
      const command = parts[0];

      switch (command) {
        case 'newmtl':
          if (currentMaterial && currentMaterialName) {
            materials[currentMaterialName] = this.createThreeMaterialWithLocalTextures(currentMaterial, textureUrlMap);
          }
          currentMaterialName = parts[1];
          currentMaterial = {
            name: currentMaterialName,
            ambient: new THREE.Color(0.2, 0.2, 0.2),
            diffuse: new THREE.Color(0.8, 0.8, 0.8),
            specular: new THREE.Color(1.0, 1.0, 1.0),
            emissive: new THREE.Color(0.0, 0.0, 0.0),
            shininess: 30,
            opacity: 1.0
          };
          break;

        case 'Ka': // Ambient color
          if (currentMaterial && parts.length >= 4) {
            currentMaterial.ambient.setRGB(
              parseFloat(parts[1]),
              parseFloat(parts[2]),
              parseFloat(parts[3])
            );
          }
          break;

        case 'Kd': // Diffuse color
          if (currentMaterial && parts.length >= 4) {
            currentMaterial.diffuse.setRGB(
              parseFloat(parts[1]),
              parseFloat(parts[2]),
              parseFloat(parts[3])
            );
          }
          break;

        case 'Ks': // Specular color
          if (currentMaterial && parts.length >= 4) {
            currentMaterial.specular.setRGB(
              parseFloat(parts[1]),
              parseFloat(parts[2]),
              parseFloat(parts[3])
            );
          }
          break;

        case 'Ke': // Emissive color
          if (currentMaterial && parts.length >= 4) {
            currentMaterial.emissive.setRGB(
              parseFloat(parts[1]),
              parseFloat(parts[2]),
              parseFloat(parts[3])
            );
          }
          break;

        case 'Ns': // Shininess
          if (currentMaterial && parts.length >= 2) {
            currentMaterial.shininess = parseFloat(parts[1]);
          }
          break;

        case 'd': // Opacity
          if (currentMaterial && parts.length >= 2) {
            currentMaterial.opacity = parseFloat(parts[1]);
          }
          break;

        case 'Tr': // Transparency (inverse of opacity)
          if (currentMaterial && parts.length >= 2) {
            currentMaterial.opacity = 1.0 - parseFloat(parts[1]);
          }
          break;

        case 'map_Kd': // Diffuse texture map
          if (currentMaterial && parts.length >= 2) {
            const textureName = parts[parts.length - 1];
            currentMaterial.diffuseMap = textureName.split(/[/\\]/).pop() || textureName;
          }
          break;

        case 'map_Bump':
        case 'bump': // Normal map
          if (currentMaterial && parts.length >= 2) {
            const textureName = parts[parts.length - 1];
            currentMaterial.normalMap = textureName.split(/[/\\]/).pop() || textureName;
          }
          break;

        case 'map_Ks': // Specular map
          if (currentMaterial && parts.length >= 2) {
            const textureName = parts[parts.length - 1];
            currentMaterial.specularMap = textureName.split(/[/\\]/).pop() || textureName;
          }
          break;
      }
    }

    if (currentMaterial && currentMaterialName) {
      materials[currentMaterialName] = this.createThreeMaterialWithLocalTextures(currentMaterial, textureUrlMap);
    }

    return materials;
  }

  private createThreeMaterialWithLocalTextures(
    mtlMaterial: MTLMaterial,
    textureUrlMap: { [key: string]: string }
  ): THREE.Material {
    let baseColor: THREE.Color;
    if (mtlMaterial.diffuseMap) {
      baseColor = new THREE.Color(0xffffff);
    } else {
      baseColor = mtlMaterial.diffuse.clone();
    }

    const material = new THREE.MeshPhongMaterial({
      name: mtlMaterial.name,
      color: baseColor,
      emissive: mtlMaterial.emissive,
      specular: mtlMaterial.specular,
      shininess: mtlMaterial.shininess,
      transparent: mtlMaterial.opacity < 1.0,
      opacity: mtlMaterial.opacity,
      side: THREE.DoubleSide,
      depthWrite: true,
      depthTest: true
    });

    const textureLoader = new THREE.TextureLoader();

    if (mtlMaterial.diffuseMap) {
      const textureName = mtlMaterial.diffuseMap;
      
      let textureUrl = textureUrlMap[textureName];
      
      if (!textureUrl) {
        const baseTextureName = textureName.split(/[/\\]/).pop() || textureName;
        textureUrl = textureUrlMap[baseTextureName];
      }
      
      if (!textureUrl) {
        const lowerTextureName = textureName.toLowerCase();
        const matchingKey = Object.keys(textureUrlMap).find(key => 
          key.toLowerCase() === lowerTextureName || 
          key.toLowerCase().includes(lowerTextureName.split(/[/\\]/).pop()?.toLowerCase() || '')
        );
        if (matchingKey) {
          textureUrl = textureUrlMap[matchingKey];
        }
      }
      
      if (textureUrl) {
        textureLoader.load(
          textureUrl,
          (texture) => {
            texture.wrapS = THREE.RepeatWrapping;
            texture.wrapT = THREE.RepeatWrapping;
            texture.flipY = true;
            texture.minFilter = THREE.LinearMipmapLinearFilter;
            texture.magFilter = THREE.LinearFilter;
            texture.anisotropy = 16;
            texture.needsUpdate = true;
            material.map = texture;
            material.color.setHex(0xffffff);
            material.needsUpdate = true;
          }
        );
      }
    }

    return material;
  }


  private processModel(model: THREE.Object3D): void {
    model.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
  }


  private getModelInfo(model: THREE.Object3D): { size: THREE.Vector3; center: THREE.Vector3; origin: THREE.Vector3 } {
    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const origin = model.userData['origin'] ?? new THREE.Vector3();
    return { size, center, origin };
  }


  private getFileExtension(filename: string): string {
    const parts = filename.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  }

  private isImageFile(filename: string): boolean {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.bmp', '.tga', '.tiff', '.webp'];
    const ext = this.getFileExtension(filename);
    return imageExtensions.includes('.' + ext);
  }

 
  isFormatSupported(filename: string): boolean {
    const ext = this.getFileExtension(filename);
    return ext === 'obj';
  }

 
  getSupportedFormats(): string[] {
    return ['.obj'];
  }
}
